<script setup lang="ts">
import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
} from 'reka-ui'
import { ref } from 'vue'

interface Props {
  show: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'danger' | 'primary'
}

withDefaults(defineProps<Props>(), {
  confirmText: '确认',
  cancelText: '取消',
  confirmVariant: 'danger',
})

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const closingAction = ref<'confirm' | 'cancel' | null>(null)

function handleConfirm() {
  closingAction.value = 'confirm'
  emit('confirm')
}

function handleCancel() {
  closingAction.value = 'cancel'
  emit('cancel')
}

function handleOpenChange(val: boolean) {
  if (!val) {
    // 如果是通过按钮关闭的，不要再次 emit cancel
    if (closingAction.value === 'confirm' || closingAction.value === 'cancel') {
      closingAction.value = null
      return
    }
    // 如果是通过点击背景或按 ESC 关闭的，emit cancel
    emit('cancel')
  }
  else {
    // 对话框打开时，重置状态
    closingAction.value = null
  }
}
</script>

<template>
  <AlertDialogRoot :open="show" @update:open="handleOpenChange">
    <AlertDialogPortal>
      <AlertDialogOverlay
        class="fixed inset-0 z-100 bg-black/50 backdrop-blur-sm data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn"
      />
      <AlertDialogContent
        class="fixed left-1/2 top-1/2 z-100 max-w-md w-[90%] border border-neutral-200 rounded-xl bg-white p-6 shadow-xl -translate-x-1/2 -translate-y-1/2 data-[state=closed]:animate-contentHide data-[state=open]:animate-contentShow dark:border-neutral-700 dark:bg-neutral-800"
      >
        <AlertDialogTitle class="mb-4 text-lg text-neutral-800 dark:text-neutral-100">
          {{ title }}
        </AlertDialogTitle>
        <AlertDialogDescription class="mb-6 text-sm text-neutral-600 dark:text-neutral-300">
          {{ message }}
        </AlertDialogDescription>

        <div class="flex flex-row justify-end gap-3">
          <button
            class="rounded-lg bg-neutral-100 px-4 py-2 text-neutral-700 transition-colors dark:bg-neutral-700 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-600"
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
          <button
            :class="[
              'px-4 py-2 rounded-lg text-white transition-colors',
              confirmVariant === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-primary-600 hover:bg-primary-700',
            ]"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
